// name: LLMFormattedPrompt.jsx
// folderstructure: my-app/src/components

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  formatFundamentalFieldsForPrompt,
  formatTablesDataForPrompt,
  estimateTokenCount,
  checkTokenLimit,
  formatPortfolioPositionForPrompt,
  callLLM
} from '../functions/LLM_analysis_functions';

const LLMFormattedPrompt = ({
  visible,
  onClose,
  stockData,
  tablesData,
  tickerAlerts,
  pfDataAlertForTicker,
  favAlert,
  tickerPortfolioData,
  oppAlert
}) => {
  const [llmModel, setLlmModel] = useState('gemini');
  const [tokenCount, setTokenCount] = useState(0);
  const [tokenWarning, setTokenWarning] = useState(null);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [llmResponse, setLlmResponse] = useState(null);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);

  const fundamentals = formatFundamentalFieldsForPrompt(stockData?.fields || []);
  const tables = formatTablesDataForPrompt(tablesData);

  // console.log("opportunity LLMFormattedPrompt", oppAlert);

  const prompt = `
--- QUESTION ---
${question}

${tickerPortfolioData ? `--- POSITION CONTEXT ---\n${formatPortfolioPositionForPrompt(tickerPortfolioData)}` : ''}

--- FUNDAMENTAL CONTEXT ---
${fundamentals}

--- OTHER DATA CONTEXT ---
${tables}

${tickerAlerts.length ? `--- PORTFOLIO ALERTS ---\n${tickerAlerts.map(a => a.pf_alert_text).join('\n\n')}` : ''}

${pfDataAlertForTicker ? `--- DATA ALERT ---\n${pfDataAlertForTicker[4]}` : ''}

${favAlert ? `--- FAVORITES ALERT ---\n${favAlert.pf_alert_text}` : ''}

${oppAlert?.length ? `--- OPPORTUNITY ALERTS ---\n${oppAlert.map(a => a.pf_alert_text).join('\n\n')}` : ''}


`;

  useEffect(() => {
    const count = estimateTokenCount(prompt);
    setTokenCount(count);
    setTokenWarning(checkTokenLimit(prompt, llmModel));
  }, [prompt, llmModel]);

  const handleSubmit = async () => {
    setLoading(true);
    setLlmResponse(null);
    const result = await callLLM(prompt, llmModel);
    setLlmResponse(result);
    setLoading(false);
    setResponseDialogOpen(true);
  };

  return (
    <>
      <Dialog
        open={visible}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{`LLM Prompt View for ${stockData?.fields?.find(f => f.fieldName === 'ticker')?.value?.toUpperCase() || ''}`}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>LLM Model</InputLabel>
            <Select
              value={llmModel}
              onChange={(event) => setLlmModel(event.target.value)}
            >
              <MenuItem value="gemini">Gemini</MenuItem>
              <MenuItem value="deepseek-chat">DeepSeek Chat</MenuItem>
              <MenuItem value="deepseek-reasoner">DeepSeek Reasoner</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Token Count"
            value={tokenCount}
            disabled
            fullWidth
            margin="normal"
            variant="outlined"
          />

          {tokenWarning && (
            <Typography variant="body2" color="warning" style={{ marginTop: '0.25rem' }}>
              ⚠️ {tokenWarning}
            </Typography>
          )}

          <TextField
            label="Your Question"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            variant="outlined"
            placeholder="Ask anything about this stock..."
          />

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !question.trim()}
            fullWidth
            style={{ marginTop: '16px', marginBottom: '16px' }}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Submitting...' : 'Submit to LLM'}
          </Button>

          <Typography variant="h6" style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>
            Generated Prompt:
          </Typography>
          <TextField
            value={prompt}
            fullWidth
            multiline
            rows={15}
            variant="outlined"
            style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Response Modal */}
      <Dialog
        open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{`LLM Response from ${llmModel}`}</DialogTitle>
        <DialogContent>
          <Typography
            style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '0.5rem',
              borderRadius: '0.25rem',
              minHeight: '200px'
            }}
          >
            {llmResponse || 'No response received.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

};

export default LLMFormattedPrompt;
